import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { updateProfile, updatePassword } from '../services/authService';

function Section({ title, children }) {
    return (
        <div style={s.section}>
            <p style={s.sectionLabel}>{title}</p>
            <div style={s.sectionCard}>
                {children}
            </div>
        </div>
    );
}

function FieldRow({ label, icon, children }) {
    return (
        <div style={s.fieldRow}>
            <div style={s.fieldLabel}>
                <i className={`bx ${icon}`} style={s.fieldIcon}></i>
                <span style={s.fieldLabelText}>{label}</span>
            </div>
            {children}
        </div>
    );
}

function UserProfile() {
    const { user, profileData, loadProfileData } = useAuth();
    const location = useLocation();

    const initialSection = new URLSearchParams(location.search).get('section') === 'security'
        ? 'security'
        : 'info';

    const [activeSection, setActiveSection] = useState(initialSection);

    // Keep the tab in step with ?section=. The useState initializer above only
    // runs on mount, so a user already sitting on /profile who tapped
    // "Security & Password" in the profile sheet changed the URL but stayed on
    // Personal Info — the tap looked broken. React Router reuses the component
    // for a query-only change, so this has to be an effect.
    useEffect(() => {
        const next = new URLSearchParams(location.search).get('section') === 'security'
            ? 'security'
            : 'info';
        setActiveSection(next);
    }, [location.search]);

    // Profile info
    const [fullName, setFullName] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState('');

    // Password change
    const [newPass, setNewPass] = useState('');
    const [confirmPass, setConfirmPass] = useState('');
    const [showNewPass, setShowNewPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);
    const [passLoading, setPassLoading] = useState(false);
    const [passMsg, setPassMsg] = useState('');

    useEffect(() => {
        const name = profileData?.fullName || user?.user_metadata?.full_name || '';
        setFullName(name);
    }, [profileData, user]);

    const handleSaveName = async () => {
        if (!fullName.trim()) return;
        setSaving(true);
        setSaveMsg('');
        const { error } = await updateProfile(fullName.trim());
        if (error) {
            setSaveMsg({ type: 'error', text: error });
        } else {
            setSaveMsg({ type: 'success', text: 'Name updated successfully' });
            window.dispatchEvent(new Event('profile-image-updated'));
            await loadProfileData(true);
        }
        setSaving(false);
        setTimeout(() => setSaveMsg(''), 3500);
    };

    const handleChangePassword = async () => {
        if (!newPass || !confirmPass) {
            setPassMsg({ type: 'error', text: 'Please fill in all fields' });
            return;
        }
        if (newPass.length < 8) {
            setPassMsg({ type: 'error', text: 'Password must be at least 8 characters' });
            return;
        }
        if (newPass !== confirmPass) {
            setPassMsg({ type: 'error', text: 'Passwords do not match' });
            return;
        }
        setPassLoading(true);
        setPassMsg('');
        const { error } = await updatePassword(newPass);
        if (error) {
            setPassMsg({ type: 'error', text: error });
        } else {
            setPassMsg({ type: 'success', text: 'Password changed successfully' });
            setNewPass('');
            setConfirmPass('');
        }
        setPassLoading(false);
        setTimeout(() => setPassMsg(''), 4000);
    };

    const email = user?.email || '';
    const currentName = profileData?.fullName || user?.user_metadata?.full_name || '';
    const initials = currentName ? currentName.charAt(0).toUpperCase() : email.charAt(0).toUpperCase();
    const avatarUrl = profileData?.profileImage || null;
    const joinDate = user?.created_at
        ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        : '';

    const nameChanged = fullName.trim() !== currentName;

    return (
        <div style={s.screen}>
            {/* Header */}
            <div style={s.header}>
                <Link to="/welcome" style={s.backBtn}>
                    <i className="bx bx-chevron-left" style={{ fontSize: 26 }}></i>
                </Link>
                <span style={s.headerTitle}>My Profile</span>
                <div style={{ width: 36 }} />
            </div>

            {/* Avatar block */}
            <div style={s.avatarBlock}>
                {avatarUrl ? (
                    <img src={avatarUrl} alt={currentName} style={s.avatarImg} />
                ) : (
                    <div style={s.avatarPlaceholder}>{initials}</div>
                )}
                <p style={s.avatarName}>{currentName || 'User'}</p>
                <p style={s.avatarEmail}>{email}</p>
                {joinDate && <p style={s.avatarJoin}>Member since {joinDate}</p>}
            </div>

            {/* Tab toggle */}
            <div style={s.tabRow}>
                <button
                    style={{ ...s.tabBtn, ...(activeSection === 'info' ? s.tabBtnActive : {}) }}
                    onClick={() => setActiveSection('info')}
                >
                    <i className="bx bx-user" style={{ fontSize: 15, marginRight: 5 }}></i>
                    Personal Info
                </button>
                <button
                    style={{ ...s.tabBtn, ...(activeSection === 'security' ? s.tabBtnActive : {}) }}
                    onClick={() => setActiveSection('security')}
                >
                    <i className="bx bx-lock-alt" style={{ fontSize: 15, marginRight: 5 }}></i>
                    Security
                </button>
            </div>

            <div style={s.content}>
                {/* ── Personal Information ── */}
                {activeSection === 'info' && (
                    <>
                        <Section title="PERSONAL INFORMATION">
                            <FieldRow label="Full Name" icon="bx-user">
                                <div style={s.fieldInputRow}>
                                    <input
                                        type="text"
                                        value={fullName}
                                        onChange={e => setFullName(e.target.value)}
                                        style={s.input}
                                        placeholder="Your full name"
                                        maxLength={80}
                                    />
                                    {nameChanged && (
                                        <button
                                            style={saving ? { ...s.saveBtn, opacity: 0.6 } : s.saveBtn}
                                            onClick={handleSaveName}
                                            disabled={saving}
                                        >
                                            {saving ? 'Saving…' : 'Save'}
                                        </button>
                                    )}
                                </div>
                            </FieldRow>

                            <div style={s.divider} />

                            <FieldRow label="Email Address" icon="bx-envelope">
                                <div style={s.readonlyRow}>
                                    <span style={s.readonlyText}>{email}</span>
                                    <span style={s.lockedBadge}>
                                        <i className="bx bx-lock-alt" style={{ fontSize: 11 }}></i>
                                    </span>
                                </div>
                            </FieldRow>
                        </Section>

                        {saveMsg && (
                            <div style={saveMsg.type === 'success' ? s.toastSuccess : s.toastError}>
                                <i className={`bx ${saveMsg.type === 'success' ? 'bx-check-circle' : 'bx-error-circle'}`}
                                    style={{ fontSize: 16 }}></i>
                                {saveMsg.text}
                            </div>
                        )}
                    </>
                )}

                {/* ── Security ── */}
                {activeSection === 'security' && (
                    <>
                        <Section title="ACCOUNT SECURITY">
                            <FieldRow label="Email (login)" icon="bx-envelope">
                                <div style={s.readonlyRow}>
                                    <span style={s.readonlyText}>{email}</span>
                                    <span style={s.verifiedBadge}>Verified</span>
                                </div>
                            </FieldRow>

                            <div style={s.divider} />

                            <FieldRow label="Account Status" icon="bx-shield-check">
                                <div style={s.readonlyRow}>
                                    <span style={{ ...s.readonlyText, color: '#10B981' }}>Active</span>
                                </div>
                            </FieldRow>
                        </Section>

                        <Section title="CHANGE PASSWORD">
                            <div style={s.passNote}>
                                <i className="bx bx-info-circle" style={{ fontSize: 15, color: '#4DA3E0', marginRight: 7, flexShrink: 0 }}></i>
                                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>
                                    Choose a strong password. Minimum 8 characters.
                                </span>
                            </div>

                            <div style={s.passField}>
                                <label style={s.passLabel}>New Password</label>
                                <div style={s.passInputWrap}>
                                    <input
                                        type={showNewPass ? 'text' : 'password'}
                                        value={newPass}
                                        onChange={e => setNewPass(e.target.value)}
                                        style={s.passInput}
                                        placeholder="Min. 8 characters"
                                        autoComplete="new-password"
                                    />
                                    <button
                                        style={s.eyeBtn}
                                        onClick={() => setShowNewPass(v => !v)}
                                        type="button"
                                        aria-label={showNewPass ? 'Hide password' : 'Show password'}
                                    >
                                        <i className={`bx ${showNewPass ? 'bx-hide' : 'bx-show'}`} style={{ fontSize: 18 }}></i>
                                    </button>
                                </div>
                            </div>

                            <div style={{ ...s.passField, marginTop: 12 }}>
                                <label style={s.passLabel}>Confirm New Password</label>
                                <div style={s.passInputWrap}>
                                    <input
                                        type={showConfirmPass ? 'text' : 'password'}
                                        value={confirmPass}
                                        onChange={e => setConfirmPass(e.target.value)}
                                        style={s.passInput}
                                        placeholder="Re-enter new password"
                                        autoComplete="new-password"
                                    />
                                    <button
                                        style={s.eyeBtn}
                                        onClick={() => setShowConfirmPass(v => !v)}
                                        type="button"
                                        aria-label={showConfirmPass ? 'Hide' : 'Show'}
                                    >
                                        <i className={`bx ${showConfirmPass ? 'bx-hide' : 'bx-show'}`} style={{ fontSize: 18 }}></i>
                                    </button>
                                </div>
                            </div>

                            {/* Strength indicator */}
                            {newPass.length > 0 && (
                                <PasswordStrength password={newPass} />
                            )}

                            <button
                                style={passLoading ? { ...s.updatePassBtn, opacity: 0.65 } : s.updatePassBtn}
                                onClick={handleChangePassword}
                                disabled={passLoading}
                            >
                                {passLoading ? 'Updating…' : 'Update Password'}
                            </button>
                        </Section>

                        {passMsg && (
                            <div style={passMsg.type === 'success' ? s.toastSuccess : s.toastError}>
                                <i className={`bx ${passMsg.type === 'success' ? 'bx-check-circle' : 'bx-error-circle'}`}
                                    style={{ fontSize: 16 }}></i>
                                {passMsg.text}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Bottom padding */}
            <div style={{ height: 90 }} />
        </div>
    );
}

function PasswordStrength({ password }) {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    const levels = [
        { label: 'Very Weak', color: '#EF4444' },
        { label: 'Weak',      color: '#F97316' },
        { label: 'Fair',      color: '#F59E0B' },
        { label: 'Good',      color: '#3B82F6' },
        { label: 'Strong',    color: '#10B981' },
    ];
    const { label, color } = levels[Math.min(score, 4)];

    return (
        <div style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', gap: 4, marginBottom: 5 }}>
                {[0, 1, 2, 3, 4].map(i => (
                    <div key={i} style={{
                        flex: 1,
                        height: 3,
                        borderRadius: 4,
                        background: i < score ? color : 'rgba(255,255,255,0.1)',
                        transition: 'background 0.2s',
                    }} />
                ))}
            </div>
            <p style={{ fontSize: 11, color, margin: 0, fontWeight: 600 }}>{label}</p>
        </div>
    );
}

const s = {
    screen: {
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #07092A 0%, #0D1240 100%)',
        color: '#fff',
    },
    header: {
        position: 'sticky',
        top: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 16px',
        background: 'rgba(7, 9, 42, 0.92)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
    },
    backBtn: {
        width: 36,
        height: 36,
        borderRadius: 10,
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.09)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        flexShrink: 0,
    },
    headerTitle: {
        fontSize: 17,
        fontWeight: 700,
        color: '#fff',
        letterSpacing: 0.2,
    },

    /* Avatar block */
    avatarBlock: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '28px 16px 20px',
        textAlign: 'center',
    },
    avatarImg: {
        width: 80,
        height: 80,
        borderRadius: '50%',
        objectFit: 'cover',
        border: '3px solid rgba(25,191,177,0.4)',
        marginBottom: 12,
    },
    avatarPlaceholder: {
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #0072CE 0%, #19bfb1 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 30,
        fontWeight: 800,
        color: '#fff',
        marginBottom: 12,
        border: '3px solid rgba(0,114,206,0.35)',
    },
    avatarName: {
        fontSize: 20,
        fontWeight: 700,
        color: '#fff',
        margin: '0 0 4px',
    },
    avatarEmail: {
        fontSize: 13,
        color: 'rgba(255,255,255,0.45)',
        margin: '0 0 4px',
    },
    avatarJoin: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.3)',
        margin: 0,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
    },

    /* Tab toggle */
    tabRow: {
        display: 'flex',
        margin: '0 16px 20px',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 14,
        padding: 4,
        gap: 4,
    },
    tabBtn: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '9px 8px',
        borderRadius: 10,
        border: 'none',
        background: 'transparent',
        color: 'rgba(255,255,255,0.45)',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    tabBtnActive: {
        background: 'rgba(0,114,206,0.2)',
        color: '#4DA3E0',
        border: '1px solid rgba(0,114,206,0.3)',
    },

    /* Content */
    content: {
        padding: '0 16px',
    },

    /* Section */
    section: {
        marginBottom: 16,
    },
    sectionLabel: {
        fontSize: 11,
        fontWeight: 600,
        color: 'rgba(255,255,255,0.3)',
        letterSpacing: 1,
        textTransform: 'uppercase',
        marginBottom: 8,
        paddingLeft: 4,
    },
    sectionCard: {
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 18,
        overflow: 'hidden',
    },

    /* Field row */
    fieldRow: {
        padding: '14px 16px',
    },
    fieldLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        marginBottom: 8,
    },
    fieldIcon: {
        fontSize: 15,
        color: 'rgba(255,255,255,0.4)',
    },
    fieldLabelText: {
        fontSize: 11,
        fontWeight: 600,
        color: 'rgba(255,255,255,0.4)',
        textTransform: 'uppercase',
        letterSpacing: 0.6,
    },
    fieldInputRow: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
    },
    input: {
        flex: 1,
        background: 'rgba(255,255,255,0.07)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 10,
        padding: '10px 14px',
        fontSize: 15,
        color: '#fff',
        outline: 'none',
        fontFamily: 'Poppins, sans-serif',
    },
    saveBtn: {
        background: 'linear-gradient(135deg, #0092E0 0%, #0058B8 100%)',
        border: 'none',
        borderRadius: 10,
        padding: '10px 16px',
        fontSize: 13,
        fontWeight: 700,
        color: '#fff',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    divider: {
        height: 1,
        background: 'rgba(255,255,255,0.06)',
        margin: '0 16px',
    },
    readonlyRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    readonlyText: {
        fontSize: 14,
        color: 'rgba(255,255,255,0.65)',
    },
    lockedBadge: {
        background: 'rgba(255,255,255,0.07)',
        color: 'rgba(255,255,255,0.35)',
        borderRadius: 6,
        padding: '3px 8px',
        fontSize: 11,
        display: 'flex',
        alignItems: 'center',
    },
    verifiedBadge: {
        background: 'rgba(16,185,129,0.15)',
        color: '#10B981',
        borderRadius: 6,
        padding: '3px 10px',
        fontSize: 11,
        fontWeight: 600,
        border: '1px solid rgba(16,185,129,0.25)',
    },

    /* Password section */
    passNote: {
        display: 'flex',
        alignItems: 'flex-start',
        padding: '12px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
    },
    passField: {
        padding: '14px 16px 0',
    },
    passLabel: {
        fontSize: 11,
        fontWeight: 600,
        color: 'rgba(255,255,255,0.4)',
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        marginBottom: 8,
        display: 'block',
    },
    passInputWrap: {
        position: 'relative',
    },
    passInput: {
        width: '100%',
        background: 'rgba(255,255,255,0.07)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 10,
        padding: '11px 44px 11px 14px',
        fontSize: 15,
        color: '#fff',
        outline: 'none',
        fontFamily: 'Poppins, sans-serif',
        boxSizing: 'border-box',
    },
    eyeBtn: {
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'none',
        border: 'none',
        color: 'rgba(255,255,255,0.35)',
        cursor: 'pointer',
        padding: 2,
        display: 'flex',
        alignItems: 'center',
    },
    updatePassBtn: {
        width: 'calc(100% - 32px)',
        margin: '16px 16px',
        background: 'linear-gradient(135deg, #0092E0 0%, #0058B8 100%)',
        border: 'none',
        borderRadius: 12,
        padding: '13px 0',
        fontSize: 15,
        fontWeight: 700,
        color: '#fff',
        cursor: 'pointer',
        display: 'block',
    },

    /* Toast messages */
    toastSuccess: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: 'rgba(16,185,129,0.12)',
        border: '1px solid rgba(16,185,129,0.3)',
        color: '#10B981',
        borderRadius: 12,
        padding: '12px 14px',
        fontSize: 13,
        fontWeight: 600,
        marginBottom: 12,
    },
    toastError: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: 'rgba(239,68,68,0.1)',
        border: '1px solid rgba(239,68,68,0.25)',
        color: '#EF4444',
        borderRadius: 12,
        padding: '12px 14px',
        fontSize: 13,
        fontWeight: 600,
        marginBottom: 12,
    },
};

export default UserProfile;
